import React, { useMemo } from 'react';

interface AnimatedCirclesProps {
  className?: string;
  variant?: 'hero' | 'section' | 'background';
  color?: 'green' | 'blue' | 'white' | 'gray';
  density?: 'low' | 'medium' | 'high';
  animated?: boolean;
  parallax?: boolean; // nutzt größere Auslenkungen
}

interface Circle {
  id: number;
  size: number;
  x: number;
  y: number;
  opacity: number;
  filled: boolean;
  animationDelay: number;
  animationDuration: number;
  strokeWidth?: number;
  // neue Bewegungsparameter
  ampX: number;
  ampY: number;
  rot: number;
  scaleJitter: number;
  keyframesName: string;
}

const AnimatedCircles: React.FC<AnimatedCirclesProps> = ({
  className = '',
  variant = 'hero',
  color = 'gray',
  density = 'high',
  animated = true,
  parallax = false,
}) => {
  // Farben
  const colorConfig = useMemo(() => {
    const colors = {
      green: {
        fill: 'rgb(134, 239, 172)',
        fillDark: 'rgb(74, 222, 128)', 
        stroke: 'rgb(134, 239, 172)',
        gradient: ['#86efac', '#4ade80', '#22c55e'],
        glow: 'rgba(34, 197, 94, 0.6)'
      },
      blue: {
        fill: 'rgb(96, 165, 250)',
        fillDark: 'rgb(59, 130, 246)',
        stroke: 'rgb(96, 165, 250)',
        gradient: ['#60a5fa', '#3b82f6', '#2563eb'],
        glow: 'rgba(59, 130, 246, 0.6)'
      },
      white: {
        fill: 'rgba(255, 255, 255, 0.9)',
        fillDark: 'rgba(255, 255, 255, 0.7)',
        stroke: 'rgba(255, 255, 255, 0.8)',
        gradient: ['#ffffff', '#f3f4f6', '#e5e7eb'],
        glow: 'rgba(255, 255, 255, 0.4)'
      },
      gray: {
        fill: 'rgba(156, 163, 175, 0.5)',
        fillDark: 'rgba(107, 114, 128, 0.5)',
        stroke: 'rgba(156, 163, 175, 0.6)',
        gradient: ['#9ca3af', '#6b7280', '#4b5563'],
        glow: 'rgba(156, 163, 175, 0.4)'
      }
    } as const;
    return colors[color];
  }, [color]);

  // Anzahl Kreise
  const circleCount = useMemo(() => {
    const counts = { low: 5, medium: 8, high: 12 } as const;
    return counts[density];
  }, [density]);

  // deterministische Kreise mit sanfter Drift
  const circles: Circle[] = useMemo(() => {
    const generateCircles = (): Circle[] => {
      const out: Circle[] = [];
      const seed = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          hash = ((hash << 5) - hash) + str.charCodeAt(i);
          hash |= 0;
        }
        return Math.abs(hash);
      };

      for (let i = 0; i < circleCount; i++) {
        const s = seed(`circle-${i}-${variant}`);
        const size = 100 + (s % 350); // 100-450px
        const x = (s * 7) % 100;      // 0-100%
        const y = (s * 13) % 100;     // 0-100%
        const opacity = 0.25 + ((s % 50) / 100); // 0.25-0.75
        const filled = s % 3 !== 0; // 2/3 gefüllt
        const animationDelay = (s % 5000) / 1000; // 0-5s
        const animationDuration = 30 + (s % 40); // 30-70s
        const strokeWidth = filled ? 0 : 2 + (s % 2); // 2-3px

        // Bewegungsamplituden (parallax => größer)
        const baseAx = 20 + (s % 60); // 20-80px
        const baseAy = 15 + (s % 50); // 15-65px
        const ampX = parallax ? Math.round(baseAx * 1.6) : baseAx;
        const ampY = parallax ? Math.round(baseAy * 1.6) : baseAy;
        const rot = ((s % 7) - 3) * 0.6; // leichte Rotation ~ -1.8..+2.4°
        const scaleJitter = 0.02 + ((s % 6) / 100); // 0.02..0.08
        const keyframesName = `floatAround-${variant}-${i}`;

        out.push({
          id: i,
          size,
          x,
          y,
          opacity,
          filled,
          animationDelay,
          animationDuration,
          strokeWidth,
          ampX,
          ampY,
          rot,
          scaleJitter,
          keyframesName,
        });
      }

      return out;
    };

    return generateCircles();
  }, [circleCount, variant, parallax]);

  // Container Styles
  const containerStyles = useMemo(() => {
    const base = 'absolute inset-0 overflow-hidden pointer-events-none';
    const variants = {
      hero: 'z-0',
      section: 'z-0 opacity-60',
      background: 'z-0 opacity-30',
    } as const;
    return `${base} ${variants[variant]} ${className}`;
  }, [variant, className]);

  // dynamische Keyframes pro Kreis, damit Bewegung nicht einheitlich wirkt
  const dynamicKeyframesCss = useMemo(() => {
    return circles.map(c => `
      @keyframes ${c.keyframesName} {
        0%   { transform: translate3d(0px, 0px, 0) rotate(0deg) scale(1); }
        20%  { transform: translate3d(${(c.ampX * 0.5).toFixed(1)}px, ${(-c.ampY * 0.6).toFixed(1)}px, 0) rotate(${(c.rot * 0.5).toFixed(2)}deg) scale(${(1 + c.scaleJitter * 0.3).toFixed(3)}); }
        40%  { transform: translate3d(${(-c.ampX).toFixed(1)}px, ${(-c.ampY * 0.2).toFixed(1)}px, 0) rotate(${(-c.rot * 0.3).toFixed(2)}deg) scale(${(1 - c.scaleJitter * 0.2).toFixed(3)}); }
        60%  { transform: translate3d(${(-c.ampX * 0.3).toFixed(1)}px, ${(c.ampY * 0.7).toFixed(1)}px, 0) rotate(${(c.rot * 0.8).toFixed(2)}deg) scale(${(1 + c.scaleJitter * 0.1).toFixed(3)}); }
        80%  { transform: translate3d(${(c.ampX * 0.6).toFixed(1)}px, ${(c.ampY * 0.2).toFixed(1)}px, 0) rotate(${(-c.rot * 0.2).toFixed(2)}deg) scale(1); }
        100% { transform: translate3d(0px, 0px, 0) rotate(0deg) scale(1); }
      }
    `).join('\n');
  }, [circles]);

  return (
    <div className={containerStyles} aria-hidden="true">
      <svg
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
      >
        <defs>
          <radialGradient id={`gradient-${color}`}>
            <stop offset="0%" stopColor={colorConfig.gradient[0]} stopOpacity="0.8" />
            <stop offset="40%" stopColor={colorConfig.gradient[1]} stopOpacity="0.5" />
            <stop offset="100%" stopColor={colorConfig.gradient[2]} stopOpacity="0.08" />
          </radialGradient>
          <filter id={`glow-${color}`}>
            <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {circles.map((circle) => (
          <g key={circle.id}>
            <circle
              cx={`${circle.x}%`}
              cy={`${circle.y}%`}
              r={circle.size / 2}
              fill={circle.filled ? `url(#gradient-${color})` : 'none'}
              stroke={!circle.filled ? colorConfig.stroke : 'none'}
              strokeWidth={circle.strokeWidth}
              opacity={circle.opacity}
              filter={circle.filled ? `url(#glow-${color})` : undefined}
              className={animated ? 'animate-circle' : ''}
              style={
                animated
                  ? {
                      animation: `\n                        ${circle.keyframesName} \n                        ${circle.animationDuration}s \n                        ${circle.animationDelay}s \n                        infinite linear\n                      `,
                      transformOrigin: 'center',
                      filter: circle.filled ? `drop-shadow(0 0 20px ${colorConfig.glow})` : undefined,
                    }
                  : {}
              }
            />
          </g>
        ))}
      </svg>

      {/* Globale, sehr sanfte Easing + Performance Hints */}
      <style jsx>{`
        .animate-circle { will-change: transform, filter; transition: transform 0.3s ease; }
        * { animation-timing-function: cubic-bezier(0.4, 0, 0.2, 1); }
      `}</style>

      {/* dynamisch generierte Keyframes pro Kreis */}
      <style jsx>{dynamicKeyframesCss}</style>
    </div>
  );
};

export default AnimatedCircles;
